/**
 * TermsPage - Terms of Service
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PublicLayout, Container } from '../../components/layout';

export const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      <Container className="py-16">
        <div className="max-w-3xl mx-auto">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-8 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              Last updated: February 2026
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 mb-4">
              By accessing or using DARAK ("Platform"), you agree to be bound by these Terms of Service. If you do not agree, please do not use our services.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
            <p className="text-gray-600 mb-4">
              DARAK is a platform that connects travelers with unique accommodations across Lebanon. We facilitate bookings between guests and hosts but do not own or manage any listed properties.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. Account Registration</h2>
            <p className="text-gray-600 mb-4">
              To use certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Provide accurate and complete information</li>
              <li>Keep your login credentials secure</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Be at least 18 years old to create an account</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Booking & Payments</h2>
            <p className="text-gray-600 mb-4">
              When you book a stay through DARAK, you enter into an agreement with the host. DARAK facilitates the payment process via Stripe. The price you see is the price you pay. Guests pay no additional service fees. All prices are displayed in USD.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Cancellation Policy</h2>
            <p className="text-gray-600 mb-4">
              Cancellation policies vary by listing and are set by individual hosts. The applicable cancellation policy is displayed on each listing before you book. Please review it carefully.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. User Conduct</h2>
            <p className="text-gray-600 mb-4">
              You agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4">
              <li>Use the platform for any unlawful purpose</li>
              <li>Post false, misleading, or fraudulent content</li>
              <li>Harass, threaten, or discriminate against other users</li>
              <li>Attempt to circumvent payments outside the platform</li>
              <li>Scrape or collect data from the platform without permission</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Intellectual Property</h2>
            <p className="text-gray-600 mb-4">
              All content on DARAK, including logos, text, graphics, and software, is the property of DARAK or its licensors. You may not reproduce, distribute, or create derivative works without our written consent.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="text-gray-600 mb-4">
              DARAK acts as an intermediary between guests and hosts. We are not liable for the condition of properties, the conduct of hosts or guests, or any damages arising from your use of the platform, to the maximum extent permitted by law.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Dispute Resolution</h2>
            <p className="text-gray-600 mb-4">
              In the event of a dispute between a guest and host, DARAK may mediate at its discretion. We encourage users to communicate directly to resolve issues. Any legal disputes shall be governed by the laws of Lebanon.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-600 mb-4">
              We may update these terms from time to time. Continued use of the platform after changes constitutes acceptance of the revised terms. We will notify you of material changes via email or platform notification.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Contact</h2>
            <p className="text-gray-600 mb-4">
              For questions about these terms, contact us at{' '}
              <a href="mailto:hello@kodefoundry.com" className="text-primary-600 hover:underline">hello@kodefoundry.com</a>
            </p>
          </div>

          {/* Related links */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              See also:{' '}
              <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>
              {' | '}
              <Link to="/host-terms" className="text-primary-600 hover:underline">Host Terms</Link>
              {' | '}
              <Link to="/host-guidelines" className="text-primary-600 hover:underline">Community Guidelines</Link>
            </p>
          </div>
        </div>
      </Container>
    </PublicLayout>
  );
};

export default TermsPage;
